import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";
import { Sun, Wind, Waves, Tent, Copy, Check, ArrowLeft, MapPin, Flame, Sparkles } from "lucide-react";
import { withUtm } from "../lib/utm";

/**
 * /share/august-warm-weather — bright, vibrant 4-scene 9:16 Facebook reel
 * pushing August sign-ups during the summer heatwave. Sunny palette, big
 * type, one-tap CTA + QR to app.
 */
const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");
const APP_URL = withUtm(PUBLIC_URL, { source: "facebook", medium: "reel", campaign: "august-warm-weather-2026" });

const CAPTION_LONG = `☀️ The forecast is glorious. Are you outside yet?

August in the UK doesn't happen twice. Take the tent out. Take the kids. Take the dog. Take a chance on a corner of Britain you've never pitched in.

→ 22,000+ UK campsites on Camp Compass
→ Filter by dogs, campfires, family, adults-only
→ Real trip stories from real campers
→ Free to browse · £1.99/mo Pro after a 14-day trial

Book something for THIS weekend. Not next month.

camp-compass.uk

#UKCamping #SummerCamping #Staycation #CampCompass #GetOutside`;

const CAPTION_SHORT = `☀️ Warm weather is HERE.
22k UK campsites · one tap · one tent · one weekend.
camp-compass.uk`;

function Scene({ children, bg, testid, label }) {
  return (
    <div className="flex flex-col gap-2 items-center">
      <div className="text-[10px] mono uppercase tracking-[0.25em] text-[#f59e0b]">{label}</div>
      <section
        className="relative w-full mx-auto flex flex-col justify-between text-white overflow-hidden shadow-[0_4px_0_0_#0e0e0e]"
        style={{ aspectRatio: "9 / 16", maxWidth: 380, background: bg }}
        data-testid={testid}
      >
        {children}
      </section>
    </div>
  );
}

function Sticker({ children, color = "#facc15", textColor = "#0e0e0e" }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.22em]"
      style={{ background: color, color: textColor }}
    >
      {children}
    </span>
  );
}

function CaptionBox({ label, text }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="border hairline bg-white p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="eyebrow">{label}</div>
        <button
          onClick={() => navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })}
          className="inline-flex items-center gap-1 text-xs mono uppercase tracking-wider text-[#f59e0b] hover:text-[#0e0e0e]"
          data-testid={`august-caption-copy-${label.split(" ")[0].toLowerCase()}`}
        >
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="whitespace-pre-wrap text-xs text-[#333] leading-relaxed font-sans">{text}</pre>
    </div>
  );
}

export default function AugustWarmWeatherReel() {
  return (
    <main className="min-h-screen bg-[#fafafa] px-4 sm:px-8 py-8 sm:py-12" data-testid="reel-august-warm-weather">
      <div className="max-w-5xl mx-auto">
        <Link to="/launch-pack" className="inline-flex items-center gap-1.5 text-xs mono uppercase tracking-wider text-[#0e0e0e] hover:text-[#f59e0b] mb-6">
          <ArrowLeft className="w-3 h-3" /> Back to Launch Pack
        </Link>
        <header className="mb-8">
          <div className="eyebrow text-[#f59e0b] mb-2">Facebook reel · 9:16 · 4 scenes · warm-weather push</div>
          <h1 className="font-display text-3xl sm:text-4xl leading-tight tracking-tight mb-2">August warm-weather sign-up push</h1>
          <p className="text-sm text-[#4a4a4a] max-w-2xl">Bright, vibrant, sunshine palette. Aimed at UK Facebook camping / staycation groups. QR to the app, one-tap sign-up CTA. Best posted midweek so it&rsquo;s in-feed for weekend planning.</p>
        </header>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {/* SCENE 01 — HOOK */}
          <Scene bg="linear-gradient(180deg,#facc15 0%,#ef4444 100%)" testid="august-scene-01" label="01 · Hook">
            <div className="p-5 flex flex-col h-full justify-between">
              <Sticker><Sun className="w-3 h-3" strokeWidth={2.5} />UK · August 2026</Sticker>
              <div>
                <div className="text-5xl sm:text-6xl font-black leading-[0.95] mb-4 tracking-tight drop-shadow-md">The forecast<br />is glorious.<br /><span className="text-[#0e0e0e]">Are you<br />outside yet?</span></div>
                <div className="text-sm mono uppercase tracking-widest text-white/90">→ Swipe up</div>
              </div>
            </div>
          </Scene>

          {/* SCENE 02 — WHY GO */}
          <Scene bg="linear-gradient(180deg,#38bdf8 0%,#059669 100%)" testid="august-scene-02" label="02 · Why go">
            <div className="p-5 flex flex-col h-full justify-between">
              <Sticker color="#fef3c7" textColor="#ea580c"><Sparkles className="w-3 h-3" strokeWidth={2.5} />Take advantage</Sticker>
              <div className="space-y-3">
                {[
                  { icon: Sun, label: "Long, dry evenings" },
                  { icon: Waves, label: "Coast & lakes swim-warm" },
                  { icon: Wind, label: "Sea breeze on the tent" },
                  { icon: Tent, label: "One tent, one weekend" },
                ].map(({ icon: Icon, label }, i) => (
                  <div key={i} className="flex items-center gap-3 bg-black/25 backdrop-blur px-3 py-2.5 rounded-lg">
                    <Icon className="w-5 h-5 text-[#facc15] flex-shrink-0" strokeWidth={2.5} />
                    <span className="text-base font-black text-white leading-tight">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </Scene>

          {/* SCENE 03 — THE APP */}
          <Scene bg="linear-gradient(180deg,#0e0e0e 0%,#f59e0b 100%)" testid="august-scene-03" label="03 · The app">
            <div className="p-5 flex flex-col h-full justify-between">
              <Sticker><MapPin className="w-3 h-3" strokeWidth={2.5} />Camp Compass</Sticker>
              <div>
                <div className="text-4xl sm:text-5xl font-black leading-tight mb-3 drop-shadow-md">22,000+<br />UK sites.<br /><span className="text-[#facc15]">This weekend.</span></div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#facc15]" strokeWidth={2.5} /> Dogs · fires · family filters</li>
                  <li className="flex items-center gap-2"><Flame className="w-4 h-4 text-[#facc15]" strokeWidth={2.5} /> Real trip stories from campers</li>
                  <li className="flex items-center gap-2"><Tent className="w-4 h-4 text-[#facc15]" strokeWidth={2.5} /> Free · Pro from £1.99/mo</li>
                </ul>
              </div>
              <div className="text-sm mono uppercase tracking-widest text-white/90">camp-compass.uk</div>
            </div>
          </Scene>

          {/* SCENE 04 — CTA */}
          <Scene bg="linear-gradient(180deg,#facc15 0%,#f97316 60%,#ef4444 100%)" testid="august-scene-04" label="04 · Go">
            <div className="p-5 flex flex-col h-full justify-between items-center text-center">
              <Sticker color="#0e0e0e" textColor="#facc15">Great time to sign up</Sticker>
              <div className="flex flex-col items-center gap-3">
                <div className="bg-white p-3 shadow-[0_4px_0_0_#0e0e0e]">
                  <QRCodeSVG value={APP_URL} size={140} includeMargin={false} level="M" />
                </div>
                <div className="text-white text-xs mono uppercase tracking-widest">Scan · sign up · pitch</div>
              </div>
              <div>
                <div className="text-3xl font-black leading-tight drop-shadow-md text-[#0e0e0e]">Book this<br />weekend.</div>
                <div className="text-xs mono uppercase tracking-widest mt-3 text-white/95">camp-compass.uk</div>
              </div>
            </div>
          </Scene>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-10">
          <CaptionBox label="Long caption" text={CAPTION_LONG} />
          <CaptionBox label="Short caption" text={CAPTION_SHORT} />
        </div>

        <div className="text-xs mono text-[#666]">
          <Link to="/launch-pack" className="underline hover:text-[#f59e0b]">Back to Launch Pack</Link>
        </div>
      </div>
    </main>
  );
}
